import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface LoginForm {
  email: string
  password: string
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false)
  // null while unknown, so the sign-up prompt never flashes before we know
  // whether the backend would actually accept a registration.
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(null)
  const { login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    authAPI
      .registrationStatus()
      .then((res) => !cancelled && setRegistrationOpen(res.data.open))
      // On failure, assume closed. Showing a link that 403s is worse than
      // hiding one that would have worked.
      .catch(() => !cancelled && setRegistrationOpen(false))
    return () => {
      cancelled = true
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    setLoading(true)
    try {
      await login(data.email, data.password)
      toast.success('Signed in')
      navigate('/dashboard')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Sign in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-4 text-center">
          {/* Vertical lockup here: it is the variant designed to sit above
              centred text, unlike the horizontal one used in the sidebar. */}
          <img
            src="/logo-vertical.svg"
            alt="VirtueNetz"
            width={298}
            height={308}
            className="h-auto w-[120px]"
          />
          <p className="text-sm text-muted-foreground">AI Chatbot admin console</p>
        </div>

        <Card>
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-base">Sign in</CardTitle>
            <CardDescription>Enter your credentials to continue.</CardDescription>
          </CardHeader>

          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  aria-invalid={!!errors.email}
                  {...register('email', {
                    required: 'Enter your email address',
                    pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email address' },
                  })}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  aria-invalid={!!errors.password}
                  {...register('password', {
                    required: 'Enter your password',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Signing in' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {registrationOpen && (
          <p className="text-center text-sm text-muted-foreground">
            No account yet?{' '}
            <Link
              to="/register"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Create one
            </Link>
            <span className="mt-1 block text-xs">The first account created becomes the admin.</span>
          </p>
        )}

        {registrationOpen === false && (
          <p className="text-center text-xs text-muted-foreground">
            Need access? Ask an administrator to create your account.
          </p>
        )}
      </div>
    </div>
  )
}

export default Login
